'use client'

import { Bell, CheckCheck, Trash2 } from 'lucide-react'
import { useRouter, usePathname } from 'next/navigation'
import { memo, useCallback, useEffect, useRef, useState } from 'react'

import { counterStyles, type CounterPriority } from './notificationBell.constants'
import { NotificationItem } from './NotificationItem'
import { Button } from '@fp/ui-kit'
import { useNotifications } from '@hooks/useNotifications'
import type { Notification } from '@hooks/useNotifications'
import { cn } from '@lib/cn'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@ui/DropDownMenu'

export const NotificationBell = memo(() => {
  const { notifications, unreadCount, dismiss, clearAll, markRead, markUnread, markAllRead } = useNotifications()
  const router = useRouter()
  const pathname = usePathname()

  const prevCountRef = useRef<number | null>(null)
  const [animationKey, setAnimationKey] = useState(0)

  useEffect(() => {
    if (prevCountRef.current !== null && unreadCount > prevCountRef.current) {
      setAnimationKey((k) => k + 1)
    }
    prevCountRef.current = unreadCount
  }, [unreadCount])

  const handleToggleRead = useCallback(
    (notification: Notification) => {
      if (notification.readAt) {
        markUnread(notification.id)
      } else {
        markRead(notification.id)
      }
    },
    [markRead, markUnread],
  )

  const priority: CounterPriority = notifications.some((n) => n.type === 'error')
    ? 'error'
    : notifications.some((n) => n.type === 'warning')
      ? 'warning'
      : 'default'

  const counterStyle = counterStyles[priority]

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='ghost' size='sm' className='relative h-8 w-8 p-0 mr-2'>
          <Bell
            key={`bell-${animationKey}`}
            className={cn('h-4 w-4 origin-top transform-gpu', animationKey > 0 && 'animate-bell-swing')}
          />
          {unreadCount > 0 && (
            <div
              key={`badge-${animationKey}`}
              className={cn(
                'absolute -top-1 -right-1 h-4 min-w-4 px-1 text-xs font-medium',
                'flex items-center justify-center rounded-full transform-gpu',
                animationKey > 0 && 'animate-badge-pop',
                counterStyle.bg,
                counterStyle.text,
              )}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </div>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className='w-80 p-0 rounded-xl'>
        {notifications.length === 0 ? (
          <div className='p-4 text-center text-sm text-muted-foreground'>No notifications</div>
        ) : (
          <>
            <div className='flex items-center justify-between px-3 py-2 border-b border-border'>
              <span className='text-sm font-medium'>
                Notifications <span className='text-muted-foreground font-normal'>({notifications.length})</span>
              </span>
              <div className='flex gap-1'>
                {unreadCount > 0 && (
                  <Button variant='ghost' size='sm' onClick={markAllRead}>
                    <CheckCheck className='size-3' />
                    Read all
                  </Button>
                )}
                <Button variant='ghost' size='sm' onClick={clearAll}>
                  <Trash2 className='size-3' />
                  Clear
                </Button>
              </div>
            </div>
            <div className='max-h-80 overflow-y-auto'>
              {notifications.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  onDismiss={dismiss}
                  onToggleRead={handleToggleRead}
                  onMarkRead={markRead}
                  actionCtx={{ router, pathname }}
                />
              ))}
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
})
